/* =============================================================================
 * Review Wall (ifm0wyf8)
 *
 * Three scopes:
 *   - product + aggregated — the classic PDP wall: SSR renders the summary +
 *     page 1 from the product's metafields; this runtime paginates from page 2.
 *   - site  — reviews across every product; no product context, so the runtime
 *     fetches page 1 on connect via the /reviews/all app proxy.
 *   - curated — a hand-picked id set fetched by id and rendered in authored order.
 *
 * This runtime:
 *
 *   1. Hydrates state from the seed JSON blob emitted by the Liquid template.
 *   2. Handles pagination (page 2+ fetched from the app proxy API).
 *   3. Sort changes trigger a fresh API fetch and re-render.
 *   4. Rating filter (click histogram bar) narrows the list.
 *   5. Voting (helpful / not helpful) via POST to API.
 *   6. Review submission form with star rating input.
 *   7. Analytics via __spectrumAi.snippet.bind().
 *
 * No-bind fallback: if __spectrumAi is absent, SSR content remains usable.
 * Sort, pagination, and form features degrade (hidden or noop) since they
 * require the app proxy. Analytics silently skipped.
 * ============================================================================= */

;(() => {
  if (window.__sai_ifm0wyf8_initialized__) return
  window.__sai_ifm0wyf8_initialized__ = true

  const SNIPPET_ID = 'ifm0wyf8'
  const TAG = 'sai-ifm0wyf8'
  const FEATURE_SLUG = 'reviews'

  function noopTrack() {}
  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (_) {
        /* analytics is best-effort */
      }
    }
  }

  function formatDate(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${mm}/${dd}/${yyyy}`
  }

  function starsSvg(rating, size) {
    const sz = size || 14
    let html = ''
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        html += `<svg class="sai-ifm0wyf8__star-sm sai-ifm0wyf8__star-sm--full" width="${sz}" height="${sz}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>`
      } else {
        html += `<svg class="sai-ifm0wyf8__star-sm sai-ifm0wyf8__star-sm--empty" width="${sz}" height="${sz}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>`
      }
    }
    return html
  }

  function verifiedBadgeSvg() {
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>`
  }

  function buildCard(r, opts) {
    const rating = Math.round(Number(r.rating) || 0)
    const title = r.title || ''
    const body = r.body || ''
    const author = r.authorName || 'Anonymous'
    const date = formatDate(r.reviewedAt)
    const verified = r.verificationStatus === 'verified_buyer'
    const reply = r.reply
    const votes = r.voteCounts || {}
    const currentVote = r.currentCustomerVote || null

    let html = `<article class="sai-ifm0wyf8__card" data-review-id="${r.id}" data-action="card-preview">`

    const media = Array.isArray(r.media) ? r.media.filter((m) => m.url) : []
    if (media.length > 0) {
      const first = media[0]
      const reviewAttrs = ` data-review-rating="${rating}" data-review-author="${author}" data-review-title="${title}" data-review-body="${body}" data-review-date="${r.reviewedAt || ''}" data-review-verified="${r.verificationStatus || ''}"`
      html += `<div class="sai-ifm0wyf8__card-media">`
      const allUrls = JSON.stringify(media.map((m) => m.url))
      const firstType = first.mediaType || 'image'
      const heroInner =
        firstType === 'video'
          ? `<video src="${first.url}" preload="metadata" muted playsinline></video><svg class="sai-ifm0wyf8__play-overlay" width="48" height="48" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>`
          : `<img src="${first.url}" alt="" loading="lazy" onerror="this.parentElement.style.display='none'">`
      html += `<button class="sai-ifm0wyf8__card-media-hero${firstType === 'video' ? ' sai-ifm0wyf8__card-media-hero--video' : ''}" type="button" data-action="media-open" data-media-url="${first.url}" data-media-type="${firstType}" data-media-all='${allUrls.replace(/'/g, '&#39;')}'${reviewAttrs} aria-label="${firstType === 'video' ? 'Play video' : 'View image'}">${heroInner}</button>`
      if (media.length > 1) {
        html += `<span class="sai-ifm0wyf8__card-media-count"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg>+${media.length - 1}</span>`
      }
      html += '</div>'
    }

    html += `<div class="sai-ifm0wyf8__card-meta">`
    html += `<span class="sai-ifm0wyf8__card-author">${author}</span>`
    if (verified)
      html += `<span class="sai-ifm0wyf8__badge sai-ifm0wyf8__badge--verified">${verifiedBadgeSvg()}<span>Verified</span></span>`
    html += '</div>'

    if (date) html += `<time class="sai-ifm0wyf8__card-date">${date}</time>`

    html += `<div class="sai-ifm0wyf8__card-stars" aria-label="${rating} out of 5 stars">${starsSvg(rating, 16)}</div>`

    if (title) html += `<h3 class="sai-ifm0wyf8__card-title">${title}</h3>`
    if (body) html += `<div class="sai-ifm0wyf8__card-body">${body}</div>`

    const isLoggedIn = !!window.__spectrumAi?.customer
    if (isLoggedIn) {
      html += `<div class="sai-ifm0wyf8__votes">`
      const helpStyle = opts?.helpfulStyle || 'text_icon'
      if (helpStyle === 'text_icon')
        html += `<span class="sai-ifm0wyf8__votes-label">Helpful?</span>`
      const helpfulCount = votes.helpful || 0
      const notHelpfulCount = votes.not_helpful || 0
      html += `<button class="sai-ifm0wyf8__vote-btn${currentVote === 'helpful' ? ' sai-ifm0wyf8__vote-btn--active' : ''}" type="button" data-action="vote" data-vote-type="helpful" data-review-id="${r.id}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg><span class="sai-ifm0wyf8__vote-divider">|</span><span class="sai-ifm0wyf8__vote-count">${helpfulCount}</span></button>`
      html += `<button class="sai-ifm0wyf8__vote-btn${currentVote === 'not_helpful' ? ' sai-ifm0wyf8__vote-btn--active' : ''}" type="button" data-action="vote" data-vote-type="not_helpful" data-review-id="${r.id}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg><span class="sai-ifm0wyf8__vote-divider">|</span><span class="sai-ifm0wyf8__vote-count">${notHelpfulCount}</span></button>`
      html += '</div>'
    }

    if (reply) {
      html += `<div class="sai-ifm0wyf8__reply">`
      html += `<span class="sai-ifm0wyf8__reply-label">${opts?.replyLabel || 'Store Reply'}</span>`
      if (reply.createdAt)
        html += `<time class="sai-ifm0wyf8__reply-date">${formatDate(reply.createdAt)}</time>`
      html += `<div class="sai-ifm0wyf8__reply-body">${reply.body}</div>`
      html += '</div>'
    }

    html += '</article>'
    return html
  }

  function buildShimmerCard(hasMedia) {
    let html = '<div class="sai-ifm0wyf8__shimmer-card">'
    if (hasMedia) {
      html += '<div class="sai-ifm0wyf8__shimmer-media"></div>'
    }
    html += '<div class="sai-ifm0wyf8__shimmer-body">'
    html += '<div class="sai-ifm0wyf8__shimmer-line sai-ifm0wyf8__shimmer-line--stars"></div>'
    html += '<div class="sai-ifm0wyf8__shimmer-line sai-ifm0wyf8__shimmer-line--short"></div>'
    html += '<div class="sai-ifm0wyf8__shimmer-line sai-ifm0wyf8__shimmer-line--long"></div>'
    html += '<div class="sai-ifm0wyf8__shimmer-line sai-ifm0wyf8__shimmer-line--long"></div>'
    html += '<div class="sai-ifm0wyf8__shimmer-line sai-ifm0wyf8__shimmer-line--medium"></div>'
    html += '</div></div>'
    return html
  }

  function buildShimmer(count, cols) {
    const colCount = cols || 1
    let html = '<div class="sai-ifm0wyf8__shimmer">'
    for (let c = 0; c < colCount; c++) {
      html += '<div class="sai-ifm0wyf8__shimmer-col">'
      const perCol = Math.ceil(count / colCount)
      for (let i = 0; i < perCol; i++) {
        html += buildShimmerCard(i % 2 === 0)
      }
      html += '</div>'
    }
    html += '</div>'
    return html
  }

  if (!customElements.get(TAG)) {
    class SaiReviews extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return
        this._initialized = true
        this._track = noopTrack
        this._emit = noopTrack
        this._page = 1
        this._sortBy = 'newest'
        this._ratingFilter = null
        this._inflight = false
        this._paginationType = this.getAttribute('data-pagination-type') || 'numbered'
        this._totalPages = 1
        this._allLoadedReviews = []

        // Scope / source. product+aggregated (the classic PDP wall) server-renders
        // page 1; every other combination has no product context, so the JS fetches
        // page 1 on connect (site → all products, curated → the referenced ids).
        this._reviewScope = this.getAttribute('data-review-scope') || 'product'
        this._reviewSource = this.getAttribute('data-review-source') || 'aggregated'
        this._isPdpMode = this._reviewScope === 'product' && this._reviewSource === 'aggregated'
        this._curatedIds = (this.getAttribute('data-curated-ids') || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        this._minRating = Number(this.getAttribute('data-min-rating')) || 0
        this._requireMedia = this.getAttribute('data-require-media') === 'true'
        this._requireBody = this.getAttribute('data-require-body') === 'true'

        this._hydrateSeed()
        this._updatePagination(this._page, this._totalPages)
        this._updateLoadMore(this._page, this._totalPages)
        this._initMasonry()
        this._initSort()
        this._initHistogramFilter()
        this._initPagination()
        this._initLoadMore()
        this._initInfiniteScroll()
        this._initWriteReviewLink()
        this._initVoting()
        this._initMediaLightbox()
        this._hydrateVoteCounts()

        // Non-PDP walls carry no server-rendered page 1 — pull it now.
        if (!this._isPdpMode) {
          this._loadPage(1, this._sortBy, this._ratingFilter)
        }
      }

      disconnectedCallback() {
        if (this._mqTablet) this._mqTablet.removeEventListener('change', this._relayoutBound)
        if (this._mqDesktop) this._mqDesktop.removeEventListener('change', this._relayoutBound)
        if (this._scrollObserver) this._scrollObserver.disconnect()
        if (this._lightbox?.open) {
          this._lightbox.close()
          document.body.style.overflow = ''
        }
      }

      setAnalytics(track, emit) {
        this._track = typeof track === 'function' ? safeTrack(track) : noopTrack
        this._emit = typeof emit === 'function' ? safeTrack(emit) : noopTrack
      }

      _hydrateSeed() {
        const container = this.closest('[data-spectrum-instance-id]') || this.parentElement
        const seedEl = container ? container.querySelector('script[data-sai-ifm0wyf8-seed]') : null
        if (!seedEl) return
        try {
          const seed = JSON.parse(seedEl.textContent)
          this._stats = seed.stats || {}
          this._config = seed.config || {}
          this._perPage = seed.perPage || 10
          this._sortBy =
            this.getAttribute('data-default-sort') || this._config.defaultStorefrontSort || 'newest'
          const totalCount = Number(this.getAttribute('data-total-count')) || 0
          this._totalPages = this._perPage > 0 ? Math.ceil(totalCount / this._perPage) : 1
          const sortSelect = this.querySelector('[data-action="sort"]')
          if (sortSelect) sortSelect.value = this._sortBy
        } catch (_) {
          /* seed parse failure — SSR state is sufficient */
        }
      }

      _getProductHandle() {
        return this.getAttribute('data-product-handle') || ''
      }

      async _hydrateVoteCounts() {
        // Only the PDP wall SSRs its cards (site/curated cards arrive from the
        // fetch already carrying their vote counts).
        if (!this._isPdpMode) return
        if (!window.__spectrumAi?.customer) return
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk?.getReviews) return
        try {
          const result = await sdk.getReviews(this._getProductHandle(), {
            page: 1,
            limit: this._perPage || 10,
            sortBy: this._sortBy,
          })
          if (!result.ok) return
          const data = result.data?.data || result.data
          const reviews = data?.reviews || []
          for (const r of reviews) {
            if (!r.id || !r.voteCounts) continue
            const cards = this.querySelectorAll(
              `[data-review-id="${r.id}"], [data-review-id="spectrum-review-${r.id}"]`,
            )
            for (const card of cards) {
              const review = card.closest('.sai-ifm0wyf8__card') || card
              const voteBtns = review.querySelectorAll('[data-action="vote"]')
              for (const vb of voteBtns) {
                const type = vb.getAttribute('data-vote-type')
                const count = r.voteCounts[type] || 0
                const countEl = vb.querySelector('.sai-ifm0wyf8__vote-count')
                if (countEl) countEl.textContent = count
                vb.classList.toggle(
                  'sai-ifm0wyf8__vote-btn--active',
                  r.currentCustomerVote === type,
                )
              }
            }
          }
        } catch (_) {
          /* best-effort hydration */
        }
      }

      _ensureProxyBase() {
        if (window.__spectrumAi?.baseProxyUrl) return
        if (!window.__spectrumAi) window.__spectrumAi = {}
        const configEl = document.querySelector('script[data-spectrum-config]')
        if (configEl) {
          try {
            const cfg = JSON.parse(configEl.textContent)
            if (cfg.baseProxyUrl) {
              window.__spectrumAi.baseProxyUrl = cfg.baseProxyUrl
              return
            }
          } catch (_) {
            /* fall through */
          }
        }
        const org = document.querySelector('[data-spectrum-org]')?.getAttribute('data-spectrum-org')
        window.__spectrumAi.baseProxyUrl = org ? `/apps/spectrum_ai_${org}` : '/apps/spectrum'
      }

      // Reorder curated results to the authored id order (the store sort does not
      // apply to a hand-picked wall).
      _orderByCurated(reviews) {
        if (!Array.isArray(reviews) || this._curatedIds.length === 0) return reviews
        const order = new Map(this._curatedIds.map((id, i) => [id, i]))
        return reviews.slice().sort((a, b) => (order.get(a.id) ?? 1e9) - (order.get(b.id) ?? 1e9))
      }

      async _fetchReviews(page, sortBy, ratingFilter) {
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk) throw new Error('Spectrum SDK not available')

        // Curated — the referenced ids, in authored order, across products.
        if (this._reviewSource === 'curated') {
          if (this._curatedIds.length === 0) {
            return {
              reviews: [],
              pagination: { page: 1, limit: this._perPage, totalCount: 0, totalPages: 0 },
            }
          }
          if (!sdk.getAllReviews) throw new Error('Spectrum SDK not available')
          const result = await sdk.getAllReviews({
            scope: 'curated',
            ids: this._curatedIds.join(','),
            page,
            limit: this._perPage,
            sortBy,
          })
          if (!result.ok) throw new Error(result.error?.message || 'Failed to fetch reviews')
          const body = result.data
          const data = body.data || body
          data.reviews = this._orderByCurated(data.reviews)
          return data
        }

        // Site — every matching review across all products.
        if (this._reviewScope === 'site') {
          if (!sdk.getAllReviews) throw new Error('Spectrum SDK not available')
          const opts = { scope: 'site', page, limit: this._perPage, sortBy }
          if (ratingFilter) opts.rating = ratingFilter
          if (this._minRating) opts.minRating = this._minRating
          if (this._requireMedia) opts.withMedia = 'true'
          if (this._requireBody) opts.requireBody = 'true'
          const result = await sdk.getAllReviews(opts)
          if (!result.ok) throw new Error(result.error?.message || 'Failed to fetch reviews')
          const body = result.data
          return body.data || body
        }

        // Product — the classic PDP wall.
        const opts = { page, limit: this._perPage, sortBy }
        if (ratingFilter) opts.rating = ratingFilter
        const result = await sdk.getReviews(this._getProductHandle(), opts)
        if (!result.ok) throw new Error(result.error?.message || 'Failed to fetch reviews')
        const body = result.data
        return body.data || body
      }

      async _loadPage(page, sortBy, ratingFilter, opts) {
        if (this._inflight) return
        this._inflight = true
        const append = opts?.append === true

        const listEl = this.querySelector('[data-slot="reviews"]')
        if (!listEl) {
          this._inflight = false
          return
        }

        const loadMoreBtn = this.querySelector('[data-action="load-more"]')
        if (!append) {
          if (this._paginationType === 'numbered' && typeof this.scrollIntoView === 'function') {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          listEl.innerHTML = buildShimmer(3, this._getColCount())
        } else if (loadMoreBtn) {
          loadMoreBtn.disabled = true
          loadMoreBtn.textContent = 'Loading...'
        }
        if (append && this._paginationType === 'infinite') {
          this._showInfiniteSpinner(true)
        }

        try {
          const data = await this._fetchReviews(page, sortBy, ratingFilter)

          this._page = data.pagination.page
          this._totalPages = data.pagination.totalPages
          const totalCount = data.pagination.totalCount

          if (data.reviews.length === 0 && !append) {
            const emptyText = this.getAttribute('data-empty-text') || 'No reviews yet.'
            listEl.innerHTML = `<p class="sai-ifm0wyf8__empty">${ratingFilter ? 'No reviews match this filter.' : emptyText}</p>`
          } else {
            const tmp = document.createElement('div')
            const cardOpts = {
              helpfulStyle: this.getAttribute('data-helpful-style') || 'text_icon',
              replyLabel: this.getAttribute('data-reply-label') || 'Store Reply',
            }
            tmp.innerHTML = data.reviews.map((r) => buildCard(r, cardOpts)).join('')
            const newCards = Array.from(tmp.children)
            if (append) {
              this._appendToMasonry(listEl, newCards)
            } else {
              this._layoutMasonry(listEl, newCards)
            }
          }

          this._updatePagination(this._page, this._totalPages)
          this._updateLoadMore(this._page, this._totalPages)

          const countEl = this.querySelector('.sai-ifm0wyf8__card-count')
          if (countEl) countEl.textContent = `Reviews (${totalCount})`

          this._track(`${FEATURE_SLUG}:page_view`, {
            page: this._page,
            sort_by: sortBy,
            rating_filter: ratingFilter || null,
            result_count: data.reviews.length,
          })
        } catch (err) {
          if (!append) {
            listEl.innerHTML = `<p class="sai-ifm0wyf8__empty">Failed to load reviews. Please try again.</p>`
          }
        }

        if (loadMoreBtn) {
          loadMoreBtn.disabled = false
          loadMoreBtn.textContent = 'Load More Reviews'
        }
        this._showInfiniteSpinner(false)
        this._inflight = false
      }

      _updatePagination(page, totalPages) {
        const paginationEl = this.querySelector('[data-slot="pagination"]')
        if (!paginationEl) return

        if (this._paginationType !== 'numbered' || totalPages <= 1) {
          paginationEl.hidden = true
          return
        }

        paginationEl.hidden = false
        const prevBtn = paginationEl.querySelector('[data-action="prev-page"]')
        const nextBtn = paginationEl.querySelector('[data-action="next-page"]')
        const infoEl = paginationEl.querySelector('[data-slot="page-info"]')

        if (prevBtn) prevBtn.disabled = page <= 1
        if (nextBtn) nextBtn.disabled = page >= totalPages
        if (infoEl) infoEl.textContent = `Page ${page} of ${totalPages}`
      }

      _updateLoadMore(page, totalPages) {
        const loadMoreEl = this.querySelector('[data-slot="load-more"]')
        if (!loadMoreEl) return
        loadMoreEl.hidden =
          (this._paginationType !== 'load_more' && this._paginationType !== 'infinite') ||
          page >= totalPages
      }

      _initLoadMore() {
        const loadMoreEl = this.querySelector('[data-slot="load-more"]')
        if (!loadMoreEl) return
        loadMoreEl.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="load-more"]')
          if (!btn || this._inflight) return
          this._loadPage(this._page + 1, this._sortBy, this._ratingFilter, { append: true })
        })
      }

      _initInfiniteScroll() {
        if (this._paginationType !== 'infinite') return
        const sentinel = document.createElement('div')
        sentinel.className = 'sai-ifm0wyf8__scroll-sentinel'
        const listEl = this.querySelector('[data-slot="reviews"]')
        if (!listEl) return
        listEl.after(sentinel)

        if (typeof IntersectionObserver === 'undefined') return
        this._scrollObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && !this._inflight && this._page < this._totalPages) {
                this._loadPage(this._page + 1, this._sortBy, this._ratingFilter, { append: true })
              }
            }
          },
          { rootMargin: '200px' },
        )
        this._scrollObserver.observe(sentinel)
      }

      _showInfiniteSpinner(show) {
        const sentinel = this.querySelector('.sai-ifm0wyf8__scroll-sentinel')
        if (!sentinel) return
        if (show) {
          sentinel.innerHTML = buildShimmer(1, this._getColCount())
        } else {
          sentinel.innerHTML = ''
        }
      }

      _getColCount() {
        const cs = getComputedStyle(this)
        if (matchMedia('(min-width: 1024px)').matches)
          return Number(cs.getPropertyValue('--sai-ifm0wyf8-cols-desktop')) || 3
        if (matchMedia('(min-width: 768px)').matches)
          return Number(cs.getPropertyValue('--sai-ifm0wyf8-cols-tablet')) || 2
        return Number(cs.getPropertyValue('--sai-ifm0wyf8-cols-mobile')) || 1
      }

      _initMasonry() {
        const grid = this.querySelector('[data-slot="reviews"]')
        if (!grid) return
        const cards = Array.from(grid.querySelectorAll('.sai-ifm0wyf8__card'))
        if (cards.length === 0) return
        grid.classList.remove('sai-ifm0wyf8__grid--ssr')
        this._layoutMasonry(grid, cards)

        this._relayoutBound = () => {
          const g = this.querySelector('[data-slot="reviews"]')
          if (!g) return
          const allCards = Array.from(g.querySelectorAll('.sai-ifm0wyf8__card'))
          if (allCards.length > 0) this._layoutMasonry(g, allCards)
        }

        this._mqTablet = matchMedia('(min-width: 768px)')
        this._mqDesktop = matchMedia('(min-width: 1024px)')
        this._mqTablet.addEventListener('change', this._relayoutBound)
        this._mqDesktop.addEventListener('change', this._relayoutBound)
      }

      _layoutMasonry(grid, cards) {
        const colCount = this._getColCount()
        grid.innerHTML = ''
        const cols = []
        for (let i = 0; i < colCount; i++) {
          const col = document.createElement('div')
          col.className = 'sai-ifm0wyf8__grid-col'
          grid.appendChild(col)
          cols.push({ el: col, h: 0 })
        }
        for (const card of cards) {
          const shortest = cols.reduce((a, b) => (a.h <= b.h ? a : b))
          shortest.el.appendChild(card)
          shortest.h += card.offsetHeight || 100
        }
      }

      _appendToMasonry(grid, newCards) {
        const cols = Array.from(grid.querySelectorAll('.sai-ifm0wyf8__grid-col'))
        if (cols.length === 0) {
          this._layoutMasonry(grid, newCards)
          return
        }
        const colData = cols.map((el) => ({ el, h: el.offsetHeight || 0 }))
        for (const card of newCards) {
          const shortest = colData.reduce((a, b) => (a.h <= b.h ? a : b))
          shortest.el.appendChild(card)
          shortest.h += card.offsetHeight || 100
        }
      }

      _initSort() {
        const sortSelect = this.querySelector('[data-action="sort"]')
        if (!sortSelect) return
        sortSelect.addEventListener('change', () => {
          this._sortBy = sortSelect.value
          this._page = 1
          this._loadPage(1, this._sortBy, this._ratingFilter)
          this._track(`${FEATURE_SLUG}:sort`, { sort_by: this._sortBy })
        })
      }

      _initHistogramFilter() {
        const histogram = this.querySelector('.sai-ifm0wyf8__histogram')
        if (!histogram) return
        histogram.addEventListener('click', (e) => {
          const row = e.target.closest('[data-rating-filter]')
          if (!row) return
          if (Number(row.getAttribute('data-count')) === 0) return
          const rating = Number(row.getAttribute('data-rating-filter'))
          if (this._ratingFilter === rating) {
            // Toggle off
            this._ratingFilter = null
            this._removeFilterIndicator()
          } else {
            this._ratingFilter = rating
            this._showFilterIndicator(rating)
          }
          this._page = 1
          this._loadPage(1, this._sortBy, this._ratingFilter)
          this._track(`${FEATURE_SLUG}:filter`, { rating_filter: this._ratingFilter })
        })
      }

      _showFilterIndicator(rating) {
        this._removeFilterIndicator()
        const controls = this.querySelector('.sai-ifm0wyf8__controls')
        if (!controls) return
        const indicator = document.createElement('div')
        indicator.className = 'sai-ifm0wyf8__active-filter'
        indicator.innerHTML = `<span>Showing ${rating}-star reviews</span><button class="sai-ifm0wyf8__clear-filter" type="button">Clear</button>`
        indicator.querySelector('.sai-ifm0wyf8__clear-filter').addEventListener('click', () => {
          this._ratingFilter = null
          this._removeFilterIndicator()
          this._page = 1
          this._loadPage(1, this._sortBy, null)
        })
        controls.after(indicator)
      }

      _removeFilterIndicator() {
        const existing = this.querySelector('.sai-ifm0wyf8__active-filter')
        if (existing) existing.remove()
      }

      _initPagination() {
        const pagination = this.querySelector('[data-slot="pagination"]')
        if (!pagination) return
        pagination.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action]')
          if (!btn || btn.disabled) return
          const action = btn.getAttribute('data-action')
          if (action === 'prev-page' && this._page > 1) {
            this._loadPage(this._page - 1, this._sortBy, this._ratingFilter)
          } else if (action === 'next-page') {
            this._loadPage(this._page + 1, this._sortBy, this._ratingFilter)
          }
        })
      }

      _initWriteReviewLink() {
        const link = this.querySelector('[data-action="write-review"]')
        if (!link) return
        link.addEventListener('click', () => {
          this._track(`${FEATURE_SLUG}:write_review_click`, {})
        })
      }

      _initMediaLightbox() {
        if (this._mediaBound) return
        this._mediaBound = true

        // Create dialog once
        const dialog = document.createElement('dialog')
        dialog.className = 'sai-ifm0wyf8__lightbox'
        dialog.setAttribute('aria-modal', 'true')
        dialog.setAttribute('aria-label', 'Media viewer')
        dialog.innerHTML = `
          <div class="sai-ifm0wyf8__lightbox-panel">
            <button class="sai-ifm0wyf8__lightbox-close" type="button" data-action="lightbox-close" aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/></svg>
            </button>
            <div class="sai-ifm0wyf8__lightbox-media-side">
              <span class="sai-ifm0wyf8__lightbox-counter" data-slot="lightbox-counter"></span>
              <button class="sai-ifm0wyf8__lightbox-nav sai-ifm0wyf8__lightbox-nav--prev" type="button" data-action="lightbox-prev" aria-label="Previous" hidden>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="sai-ifm0wyf8__lightbox-content"></div>
              <button class="sai-ifm0wyf8__lightbox-nav sai-ifm0wyf8__lightbox-nav--next" type="button" data-action="lightbox-next" aria-label="Next" hidden>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
            <div class="sai-ifm0wyf8__lightbox-review-side" data-slot="lightbox-review"></div>
          </div>
        `
        // Inline display guard: merchant theme CSS may globally restyle
        // <dialog> (e.g. dialog{display:block}), defeating the UA's
        // closed-dialog default and painting the fixed overlay over the
        // whole page while closed. Inline style wins over any stylesheet.
        dialog.style.display = 'none'
        this.appendChild(dialog)
        this._lightbox = dialog
        this._lightboxItems = []
        this._lightboxIndex = 0

        const readItem = (t) => ({
          url: t.getAttribute('data-media-url'),
          type: t.getAttribute('data-media-type') || 'image',
          rating: Number(t.getAttribute('data-review-rating')) || 0,
          author: t.getAttribute('data-review-author') || '',
          title: t.getAttribute('data-review-title') || '',
          body: t.getAttribute('data-review-body') || '',
          date: t.getAttribute('data-review-date') || '',
          verified: t.getAttribute('data-review-verified') === 'verified_buyer',
        })

        const openLightbox = () => {
          this._showLightboxSlide()
          dialog.style.display = 'flex'
          dialog.showModal()
          document.body.style.overflow = 'hidden'
        }

        this.addEventListener('click', (e) => {
          const galleryThumb = e.target.closest('[data-slot="gallery"] [data-action="media-open"]')
          if (galleryThumb) {
            this._lightboxItems = [readItem(galleryThumb)]
            this._lightboxIndex = 0
            openLightbox()
            this._track(`${FEATURE_SLUG}:media_view`, { media_index: 0 })
            return
          }

          if (e.target.closest('[data-action="vote"]')) return
          if (e.target.closest('[data-action="write-review"]')) return
          if (e.target.closest('[data-action="sort"]')) return
          if (e.target.closest('.sai-ifm0wyf8__histogram')) return
          if (e.target.closest('.sai-ifm0wyf8__pagination')) return
          if (e.target.closest('.sai-ifm0wyf8__load-more')) return

          const card = e.target.closest('[data-action="card-preview"]')
          if (!card) return

          e.preventDefault()
          e.stopPropagation()

          const hero = card.querySelector('[data-action="media-open"]')
          if (hero) {
            const allMediaAttr = hero.getAttribute('data-media-all')
            if (allMediaAttr) {
              try {
                const urls = JSON.parse(allMediaAttr)
                const base = readItem(hero)
                this._lightboxItems = urls.map((u) => {
                  const isVideo = u.match(/\.(mp4|webm|mov)(\?|$)/i)
                  return { ...base, url: u, type: isVideo ? 'video' : 'image' }
                })
              } catch (_) {
                this._lightboxItems = [readItem(hero)]
              }
            } else {
              this._lightboxItems = [readItem(hero)]
            }
          } else {
            this._lightboxItems = [
              {
                url: '',
                type: 'none',
                rating: card.querySelectorAll('.sai-ifm0wyf8__star-sm--full').length,
                author: card.querySelector('.sai-ifm0wyf8__card-author')?.textContent || '',
                title: card.querySelector('.sai-ifm0wyf8__card-title')?.textContent || '',
                body: card.querySelector('.sai-ifm0wyf8__card-body')?.textContent || '',
                date: card.querySelector('.sai-ifm0wyf8__card-date')?.textContent || '',
                verified: !!card.querySelector('.sai-ifm0wyf8__badge--verified'),
              },
            ]
          }
          this._lightboxIndex = 0
          openLightbox()
          this._track(`${FEATURE_SLUG}:card_preview`, {
            review_id: card.getAttribute('data-review-id'),
          })
        })

        const closeLightbox = () => {
          dialog.classList.add('sai-ifm0wyf8__lightbox--closing')
          setTimeout(() => {
            dialog.classList.remove('sai-ifm0wyf8__lightbox--closing')
            dialog.close()
            dialog.style.display = 'none'
            document.body.style.overflow = ''
          }, 250)
        }

        dialog.addEventListener('cancel', (e) => {
          e.preventDefault()
          closeLightbox()
        })

        dialog.addEventListener('click', (e) => {
          const action = e.target.closest('[data-action]')?.getAttribute('data-action')
          if (action === 'lightbox-close') {
            closeLightbox()
            return
          }
          if (action === 'lightbox-prev') {
            this._lightboxIndex = Math.max(0, this._lightboxIndex - 1)
            this._showLightboxSlide()
            return
          }
          if (action === 'lightbox-next') {
            this._lightboxIndex = Math.min(this._lightboxItems.length - 1, this._lightboxIndex + 1)
            this._showLightboxSlide()
            return
          }
          if (e.target === dialog) closeLightbox()
        })
      }

      _showLightboxSlide() {
        const item = this._lightboxItems[this._lightboxIndex]
        if (!item) return
        const mediaSide = this._lightbox.querySelector('.sai-ifm0wyf8__lightbox-media-side')
        const content = this._lightbox.querySelector('.sai-ifm0wyf8__lightbox-content')
        if (item.type === 'none' || !item.url) {
          if (mediaSide) mediaSide.hidden = true
          content.innerHTML = ''
        } else {
          if (mediaSide) mediaSide.hidden = false
          if (item.type === 'video') {
            content.innerHTML = `<video class="sai-ifm0wyf8__lightbox-media" src="${item.url}" controls autoplay playsinline></video>`
          } else {
            content.innerHTML = `<img class="sai-ifm0wyf8__lightbox-media" src="${item.url}" alt="" onerror="this.style.display='none'">`
          }
        }

        const reviewSide = this._lightbox.querySelector('[data-slot="lightbox-review"]')
        if (reviewSide && (item.author || item.title || item.body)) {
          const rating = Math.round(item.rating || 0)
          const date = formatDate(item.date)
          let html = `<div class="sai-ifm0wyf8__lightbox-review-meta">`
          html += `<span class="sai-ifm0wyf8__card-author">${item.author}</span>`
          if (item.verified)
            html += `<span class="sai-ifm0wyf8__badge sai-ifm0wyf8__badge--verified">${verifiedBadgeSvg()}<span>Verified</span></span>`
          html += '</div>'
          if (date) html += `<time class="sai-ifm0wyf8__lightbox-review-date">${date}</time>`
          html += `<div class="sai-ifm0wyf8__lightbox-review-stars">${starsSvg(rating, 16)}</div>`
          if (item.title)
            html += `<h3 class="sai-ifm0wyf8__lightbox-review-title">${item.title}</h3>`
          if (item.body)
            html += `<div class="sai-ifm0wyf8__lightbox-review-body">${item.body}</div>`
          reviewSide.innerHTML = html
          reviewSide.hidden = false
        } else if (reviewSide) {
          reviewSide.hidden = true
        }

        const counter = this._lightbox.querySelector('[data-slot="lightbox-counter"]')
        if (counter)
          counter.textContent = `${this._lightboxIndex + 1}/${this._lightboxItems.length}`

        const prev = this._lightbox.querySelector('[data-action="lightbox-prev"]')
        const next = this._lightbox.querySelector('[data-action="lightbox-next"]')
        if (prev) prev.hidden = this._lightboxIndex <= 0
        if (next) next.hidden = this._lightboxIndex >= this._lightboxItems.length - 1
      }

      _initVoting() {
        if (this._voteBound) return
        this._voteBound = true
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="vote"]')
          if (!btn) return
          let reviewId = btn.getAttribute('data-review-id')
          if (!reviewId) return
          const voteType = btn.getAttribute('data-vote-type')
          if (!voteType) return
          // SSR renders metaobject handle (spectrum-review-{uuid}); extract UUID
          if (reviewId.startsWith('spectrum-review-')) {
            reviewId = reviewId.replace('spectrum-review-', '')
          }
          this._vote(reviewId, voteType, btn)
        })
      }

      async _vote(reviewId, voteType, btn) {
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk) return
        btn.disabled = true
        btn.style.opacity = '0.5'
        try {
          const result = await sdk.voteOnReview(reviewId, voteType, {
            productHandle: this._getProductHandle(),
          })
          btn.disabled = false
          btn.style.opacity = ''
          if (!result.ok) return
          const voteData = result.data?.data || result.data

          const card = btn.closest('.sai-ifm0wyf8__card')
          if (!card) return

          const voteBtns = card.querySelectorAll('[data-action="vote"]')
          for (const vb of voteBtns) {
            const type = vb.getAttribute('data-vote-type')
            const count = voteData?.voteCounts?.[type] || 0
            vb.classList.toggle(
              'sai-ifm0wyf8__vote-btn--active',
              voteData?.action !== 'removed' && type === voteType,
            )
            const countEl = vb.querySelector('.sai-ifm0wyf8__vote-count')
            if (countEl) {
              countEl.textContent = count
            }
          }

          this._track(`${FEATURE_SLUG}:vote`, {
            review_id: reviewId,
            vote_type: voteType,
            action: voteData?.action,
          })
        } catch (_) {
          btn.disabled = false
          btn.style.opacity = ''
          /* vote failure is silent */
        }
      }

      applyVariant(presentation) {
        if (!presentation) return
        if (typeof presentation.heading === 'string') {
          const h = this.querySelector('.sai-ifm0wyf8__heading')
          if (h) h.textContent = presentation.heading
        }
        if (typeof presentation.write_review_label === 'string') {
          const link = this.querySelector('[data-action="write-review"]')
          if (link) link.textContent = presentation.write_review_label
        }
        if (typeof presentation.write_review_url === 'string') {
          const link = this.querySelector('[data-action="write-review"]')
          if (link) {
            // The destination "Write a Review" page identifies the product
            // via a ?product=<handle> query param. The Liquid SSR builds the
            // href with that suffix already attached; the variant carries
            // only the base URL the merchant configured. Re-attach the
            // product handle here so an experience variant swapping the URL
            // at runtime can't accidentally strip the param.
            const base = presentation.write_review_url
            const handle = this._getProductHandle()
            const sep = base.indexOf('?') >= 0 ? '&' : '?'
            link.href = handle ? `${base}${sep}product=${encodeURIComponent(handle)}` : base
          }
        }
        if (typeof presentation.empty_state_text === 'string') {
          this.setAttribute('data-empty-text', presentation.empty_state_text)
          const empty = this.querySelector('.sai-ifm0wyf8__empty')
          if (empty) empty.textContent = presentation.empty_state_text
        }
      }
    }
    customElements.define(TAG, SaiReviews)
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiIfm0wyf8 = {
      buildCard,
      buildShimmer,
      verifiedBadgeSvg,
      starsSvg,
      formatDate,
      safeTrack,
    }
  }

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    if (!api?.bind) return

    for (const node of containers) {
      const handles = api.bind(node, ({ variant }) => {
        if (!variant?.content) return
        const root = node.querySelector(TAG)
        if (root && typeof root.applyVariant === 'function') {
          root.applyVariant(variant.content)
        }
      })
      const root = node.querySelector(TAG)
      if (root && handles?.track && typeof root.setAnalytics === 'function') {
        root.setAnalytics(handles.track, handles.emit)
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
